import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/AppRoutes';
import resetIcon from '@/Assets/Images/app-reset-icon.svg?url';
import ClockIcon from '@/Assets/Images/clock-icon.svg?url';
import ContactSupportIcon from '@/Assets/Images/contact-support.svg?url';
import LanguageIcon from '@/Assets/Images/language-icon.svg?url';
import ProfileBackground from '@/Assets/Images/profile-background.svg?url';
import BackgroundImage from '@/Assets/Images/profile-screen-background.svg?url';
import SwifteeThumb from '@/Assets/Images/swiftee-thumb.png';
import NotificationPopUp from '@/components/NotificationPopUp';
import Constants from '@/Constants';
import LocationHeader from '@/pages/AtHomeScreen/components/LocationHeader';
import { GetStudentByAccessTokenResponse } from '@/services/api';
import { getIconName } from '@/utils/helper';

type StudentProfileScreenProperties = {
  expiryDay: { value: number; unit: 'days' | 'hours' | 'minutes' };
  autoSignOutDate: string;
  studentProfileData: GetStudentByAccessTokenResponse | undefined;
  onResetClick: () => void;
  onLanguageClick: () => void;
  currentStudentIndex: number;
  setCurrentStudentIndex: (index: number) => void;
  onHandleSwitchProfile: () => void;
};

export default function StudentProfileScreen({
  expiryDay,
  autoSignOutDate,
  studentProfileData,
  onResetClick,
  onLanguageClick,
  currentStudentIndex,
  setCurrentStudentIndex,
  onHandleSwitchProfile,
}: StudentProfileScreenProperties): ReactElement {
  const navigate = useNavigate();
  const { t } = useTranslation('atHome');

  const menuOptions = [
    {
      onClick: () => {
        onLanguageClick();
      },
      label: t('atHome:language'),
      icon: <img src={LanguageIcon} alt="Language" style={{ width: '24px', height: '24px' }} />,
    },
    {
      onClick: () => {
        onResetClick();
      },
      label: t('atHome:appReset'),
      icon: <img src={resetIcon} alt="reset" style={{ width: '24px', height: '24px' }} />,
    },
    {
      onClick: () => {
        window.open(Constants.CUSTOMER_SUPPORT_URL, '_blank');
      },
      label: t('atHome:contactSupport'),
      icon: (
        <img
          src={ContactSupportIcon}
          alt="support-icon"
          style={{ width: '24px', height: '24px' }}
        />
      ),
    },
  ];

  const handleProfileClick = (index: number) => {
    const student = studentProfileData?.students?.[index];
    if (student?.gradeId && student?.gradeName && student?.id && student?.name && student?.code) {
      const selectedGrade = {
        id: student.gradeId,
        name: student.gradeName,
      };
      const selectedStudent = {
        id: student.id,
        name: student.name,
        code: student.code,
      };
      localStorage.setItem('selectedGrade', JSON.stringify(selectedGrade));
      localStorage.setItem('selectedStudent', JSON.stringify(selectedStudent));
      navigate(ROUTES.STUDENT_LOGIN);
    }
  };

  const getStudentProfiles = () => {
    return (
      <div className="flex gap-4">
        {studentProfileData?.students?.map((student, index) => {
          return (
            <div key={student.id || index} className="flex flex-col gap-3">
              <button
                type="button"
                className="flex justify-center"
                // eslint-disable-next-line react/jsx-handler-names
                onClick={() => {
                  setCurrentStudentIndex(index);
                  handleProfileClick(index);
                }}
              >
                <div className="relative h-[129px] w-[129px]">
                  <img
                    src={ProfileBackground}
                    alt="profile background"
                    className="h-full w-full rounded-full object-cover"
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-6xl font-semibold text-black">
                    {getIconName(student.name || '')}
                  </span>
                </div>
              </button>
              <div className="mb-5 text-center">
                <h2 className="mb-1 text-l2 font-semibold text-neutral-white">{student.name}</h2>
                <p className="text-[1.125rem] font-normal text-neutral-grey-1">{student.code}</p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="absolute inset-0 flex h-screen w-screen flex-col bg-cover bg-no-repeat"
      style={{
        backgroundImage: `url(${BackgroundImage})`,
      }}
    >
      <LocationHeader title={`${t('atHome:yourProfile')}`} menuOptions={menuOptions} />
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <div className="rounded-[60px] bg-[#5D8CFF4D] p-2 backdrop-blur-3xl">
          <div className="flex min-h-[384px] min-w-[373px] max-w-[100%] flex-col items-center rounded-[56px] bg-white/10 p-8 shadow-2xl backdrop-blur-3xl">
            {studentProfileData?.students?.length ? getStudentProfiles() : undefined}
            <div className="mb-4 flex items-center justify-center gap-2 rounded-full bg-secondary-blue-1 p-3">
              <img src={ClockIcon} alt="clock" />
              <span className="text-base font-semibold text-secondary-blue-3">
                {t(`atHome:accessCodeExpiry_${expiryDay.unit}`, { expiryDay: expiryDay.value })}
              </span>
            </div>

            <p className="text-center text-[1.125rem] text-neutral-white">
              <span className="font-normal">{t('atHome:autoSignOut')}</span>{' '}
              <span className="font-bold">{autoSignOutDate}</span>
            </p>
          </div>
        </div>

        <div className="text-center">
          <span className="text-[1.125rem] font-medium text-neutral-white">
            {t('atHome:notYourProfile')}{' '}
          </span>
          <button
            type="button"
            onClick={onHandleSwitchProfile}
            className="text-[1.125rem] font-bold text-primary-yellow-3 underline "
          >
            {t('atHome:switchProfile')}
          </button>
        </div>
      </div>
      <NotificationPopUp
        header={`${t('atHome:profileAdded')}`}
        message={
          <p className="font-poppins text-center text-base leading-tight text-primary-blue-7">
            <span className="break-words font-bold">
              {studentProfileData?.students?.[currentStudentIndex].name}
            </span>{' '}
            {t('atHome:profileVerified')}
          </p>
        }
        notificationIcon={`${SwifteeThumb}`}
        className="bottom-16"
        headerStyle="font-lilita text-[1.75rem] font-normal text-secondary-blue-3"
        isProfileAnimation
      />
    </div>
  );
}
